import React from "react";
import LegalPage, { LegalList } from "@/components/LegalPage";

const EFFECTIVE_DATE = "September 14, 2026";

const SECTIONS = [
  {
    heading: "Agreement to Terms",
    body: [
      'These Terms of Service ("Terms") govern your access to and use of the Carivisor website, mobile application, and related services (collectively, the "Services"), operated by Ryde Excursion and Tours ("Carivisor", "we", "us", or "our").',
      "By creating an account, booking an experience, or otherwise using the Services, you agree to be bound by these Terms and by our Privacy Policy. If you do not agree, please do not use the Services.",
    ],
  },
  {
    heading: "The Carivisor Marketplace",
    body: [
      "Carivisor is a marketplace that connects travelers with tours, excursions, and experiences offered by independent third-party providers. Carivisor is not the provider of these experiences and does not own, operate, or control them.",
      "The provider is solely responsible for the quality, safety, legality, and delivery of each experience. Any contract for an experience is directly between you and the provider.",
    ],
  },
  {
    heading: "Eligibility and Accounts",
    body: [
      "You must be at least 18 years old and able to form a binding contract to use the Services. You agree to provide accurate information and to keep your account credentials secure. You are responsible for all activity that occurs under your account.",
    ],
  },
  {
    heading: "Bookings and Payments",
    body: [
      "When you book an experience, you agree to pay the listed price plus any applicable taxes and fees. Payments are processed securely through Stripe.",
      <LegalList
        key="paylist"
        items={[
          "Carivisor charges a platform commission (currently 20%) that is included in the marketplace pricing model.",
          "Prices are shown in the currency indicated on each listing and may vary by provider, date, and group size.",
          "Saved cards and one-tap checkout are provided for your convenience and can be managed in your account.",
          "A confirmed booking, along with any QR check-in code, serves as your proof of reservation.",
        ]}
      />,
    ],
  },
  {
    heading: "Cancellations and Refunds",
    body: [
      "Cancellation and refund terms may vary by experience and provider and are shown at the time of booking. Where a refund is due, it will be processed to your original payment method. Certain fees may be non-refundable to the extent permitted by law.",
    ],
  },
  {
    heading: "Loyalty Points",
    body: [
      "Carivisor may award loyalty points for eligible activity such as bookings and reviews. Points have no cash value, cannot be transferred, and may expire or be adjusted in accordance with program rules, which we may update from time to time.",
    ],
  },
  {
    heading: "User Content and Community",
    body: [
      "You may submit content such as reviews, forum posts, and photos. You retain ownership of your content but grant Carivisor a worldwide, non-exclusive, royalty-free license to host, display, and use it in connection with the Services.",
      "You are responsible for your content and must not post anything unlawful, misleading, infringing, or offensive. We may moderate, remove, or restrict content and accounts that violate these Terms or our community guidelines.",
    ],
  },
  {
    heading: "Provider Obligations",
    body: [
      "If you list experiences as a provider, you agree to provide accurate listings, hold all required licenses and insurance, comply with applicable laws, honor confirmed bookings, and deliver experiences safely and as described. Carivisor may remove listings or suspend providers that fail to meet these standards.",
    ],
  },
  {
    heading: "Prohibited Conduct",
    body: [
      "You agree not to misuse the Services, including by attempting to circumvent payments, engaging in fraud, scraping data, interfering with security, or infringing the rights of others.",
    ],
  },
  {
    heading: "Disclaimers",
    body: [
      'The Services are provided "as is" and "as available" without warranties of any kind. Carivisor does not warrant that the Services will be uninterrupted or error-free, and does not guarantee the acts or omissions of any provider or the quality of any experience.',
    ],
  },
  {
    heading: "Limitation of Liability",
    body: [
      "To the fullest extent permitted by law, Carivisor and Ryde Excursion and Tours will not be liable for any indirect, incidental, special, consequential, or punitive damages, or for any loss arising from experiences delivered by third-party providers. Our total liability for any claim relating to the Services will not exceed the amount you paid to Carivisor for the booking giving rise to the claim.",
    ],
  },
  {
    heading: "Indemnification",
    body: [
      "You agree to indemnify and hold harmless Carivisor, Ryde Excursion and Tours, and their affiliates from any claims, damages, or expenses arising out of your use of the Services, your content, or your violation of these Terms.",
    ],
  },
  {
    heading: "Termination",
    body: [
      "We may suspend or terminate your access to the Services at any time if you violate these Terms or if we discontinue the Services. You may stop using the Services and close your account at any time.",
    ],
  },
  {
    heading: "Changes to These Terms",
    body: [
      "We may update these Terms from time to time. When we make material changes, we will update the effective date above. Your continued use of the Services after changes take effect constitutes acceptance of the updated Terms.",
    ],
  },
  {
    heading: "Contact Us",
    body: [
      "Questions about these Terms can be directed to:",
      <LegalList
        key="contactlist"
        items={[
          "Company: Ryde Excursion and Tours (operating as Carivisor)",
          "Email: info@carivisor.gd",
        ]}
      />,
    ],
  },
];

export default function Terms() {
  return (
    <LegalPage
      testid="terms-page"
      eyebrow="Legal"
      title="Terms of Service"
      intro="These terms set out the rules for using Carivisor, booking experiences, and interacting with providers and other travelers."
      effectiveDate={EFFECTIVE_DATE}
      sections={SECTIONS}
      seoTitle="Terms of Service | Carivisor"
      seoDescription="Read the Carivisor Terms of Service covering bookings, payments, cancellations, loyalty points, user content, and provider obligations."
      path="/terms"
    />
  );
}
